<template>
  <div
    v-if="show"
    v-analytics="trackSearchShortcutInfoBoxImpression()"
    class="search-shortcuts-info-box"
    :class="`position-${position}`"
    data-test-id="search-shortcuts-info-box"
  >
    <div
      v-safe-html="$t(translations.intro)"
      class="search-shortcuts-info-box__top"
      data-test-id="search-shortcuts-info-box__intro"
    />
    <div
      v-for="(shortcut, index) in shortcuts"
      :key="`search-shortcut_${index}`"
      class="search-shortcuts-info-box__link-bubble-container"
    >
      <div
        class="search-shortcuts-info-box__description"
        data-test-id="search-shortcuts-info-box__description"
      >
        {{ $t("web.searchshortcutsinfobox.description") }} {{ shortcut.title }}
      </div>
      <ELinkBubble
        v-analytics.click="trackLinkBubbleClick(index)"
        href="#"
        class="search-shortcuts-info-box__link-bubble"
        data-test-id="search-shortcuts-info-box__link-bubble"
        rel="noopener"
        color="result"
        size="l"
        @click="onClick(shortcut)"
      >
        <span
          v-safe-html="createQueryWithTag(shortcut)"
          data-test-id="search-shortcuts-info-box__query"
        />
        <EIcon class="search-shortcuts-info-box__icon" icon="search" size="s" />
      </ELinkBubble>
    </div>
    <div class="search-shortcuts-info-box__more">
      <span>
        <ELink
          v-analytics.click="trackSearchShortcutMoreLinkClick()"
          :href="moreLink"
          data-test-id="search-shortcuts-info-box__more"
        >
          {{ $t(translations.more) }}
        </ELink>
      </span>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import EIcon from '~~/common/vue/components/icon';
import ELink from '~~/common/vue/components/link';
import ELinkBubble from '~~/common/vue/components/link-bubble';

import screen from '~~/common/vue/mixins/screen';

import { addTagToQuery, identifiers } from '~~/common/js/universal/search-shortcuts/utils';
import { HELPSCOUT_SEARCH_SHORTCUTS } from '~~/common/js/constants/urls';

import { searchShortcuts, adRevenueExperiment } from '~~/common/js/constants/feature-flags';
import { SEARCH_SHORTCUTS_INFO_DISPLAY } from '~~/common/js/constants/storage-keys';
import {
  trackSearchShortcutInfoBoxClick,
  trackSearchShortcutMoreLinkClick,
  trackSearchShortcutInfoBoxImpression,
} from '~~/search/common/js/client/analytics/search-shortcuts';
import { checkAdsHidden } from '~~/search/common/js/client/utils/ad-visibility';

export default {
  name: 'ESearchShortcutsInfoBox',
  components: {
    EIcon,
    ELink,
    ELinkBubble,
  },
  mixins: [screen],
  props: {
    position: {
      type: String,
      default: 'top',
    },
  },
  data() {
    return {
      experiment: null,
      identifier: identifiers[0],
      initialQueryValue: '',
      moreLink: HELPSCOUT_SEARCH_SHORTCUTS,
      shownBefore: false,
      show: false,
      adBlockDetected: false,
      trackSearchShortcutInfoBoxImpression,
      trackSearchShortcutInfoBoxClick,
      trackSearchShortcutMoreLinkClick,
    };
  },
  computed: {
    ...mapState('user', ['treeCount']),
    ...mapState([
      'queryCorrection',
      'spellingSuggestion',
    ]),
    ...mapGetters([
      'hasSidebarResults',
      'hasTextAds',
      'hasProductAds',
      'hasTourActivityAds',
      'hasInstantAnswers',
      'hasEntities',
    ]),
    ...mapGetters('unleash', ['getFeatureToggle']),
    ...mapGetters('search', ['hasMentalHealthIntent']),
    query() {
      return this.$route.query?.q?.toString() || '';
    },
    page() {
      return this.$route.query.p ? parseInt(this.$route.query.p, 10) : 0;
    },
    flagActive() {
      return this.experimentHasValue(searchShortcuts.variantA) ||
             this.experimentHasValue(searchShortcuts.variantC);
    },
    adBlockNoticeFlagActive() {
      // using variant a of our long running a/b test to roll out ad block notice
      return this.getFeatureToggle(adRevenueExperiment.flag)?.value ===
             adRevenueExperiment.variantA;
    },
    adBlockNoticeVisible() {
      return this.adBlockDetected && this.adBlockNoticeFlagActive;
    },
    shortcuts() {
      const data = [{
        title: 'Youtube',
        tag: 'yt',
      }];

      if (this.experimentHasValue(searchShortcuts.variantC)) {
        data.unshift({
          title: 'Google',
          tag: 'g',
        });
      }

      return data;
    },
    noInstantAnswers() {
      return !this.hasInstantAnswers;
    },
    noSidebarResults() {
      return !this.hasSidebarResults;
    },
    noAds() {
      return !this.hasTextAds && !this.hasProductAds;
    },
    noEntities() {
      return !this.hasEntities;
    },
    noOtherNoticeBox() {
      return !this.queryCorrection &&
        !this.spellingSuggestion &&
        !this.hasMentalHealthIntent &&
        !this.adBlockNoticeVisible;
    },
    showAtTop() {
      // if it was inserted by the SRS, it must only be shown on top
      // and if the variant for job A is active
      return this.experimentHasValue(searchShortcuts.variantA) && this.position === 'top';
    },
    showAtBottom() {
      // if it was rendered by the mainline component, it must only be
      // shown at the bottom and if the variant for job C is active
      return this.experimentHasValue(searchShortcuts.variantC) && this.position === 'bottom';
    },
    displayConditionsPassed() {
      return this.flagActive &&
        this.noOtherNoticeBox &&
        this.noInstantAnswers &&
        this.noSidebarResults &&
        this.noAds &&
        this.noEntities &&
        this.treeCount > 3 &&
        (this.showAtTop || this.showAtBottom);
    },
    translations() {
      // we need a different translation for Job C
      const isExperimentJobC = this.experimentHasValue(searchShortcuts.variantC);

      const translationPrefix = 'web.searchshortcutsinfobox';
      const translationStrings = isExperimentJobC ?
        { intro: `${translationPrefix}.alternativeIntro`, more: `${translationPrefix}.alternativeMore` } :
        { intro: `${translationPrefix}.intro`, more: `${translationPrefix}.more` };

      return translationStrings;
    },
  },
  created() {
    this.initialQueryValue = this.query;
    this.experiment = this.getFeatureToggle(searchShortcuts.flag);
  },
  mounted() {
    // check for ad block detection first while we have access to "window" in mounted().
    // we only consider detection if we received ads in the store, but they are not visible.
    const { textAdsHidden, productAdsHidden, tourActivityAdsHidden } = checkAdsHidden();
    this.adBlockDetected = (this.hasTextAds && textAdsHidden) ||
      (this.hasProductAds && productAdsHidden) ||
      (this.hasTourActivityAds && tourActivityAdsHidden);

    // evaluate all conditions to show the info box and
    // save the value so we don't need to evaluate twice
    const passed = this.displayConditionsPassed;

    // if the conditions for displaying this component didn't pass anyway
    // we must not change the shownBefore value in localStorage
    if (passed) {
      this.shownBefore = localStorage?.getItem(SEARCH_SHORTCUTS_INFO_DISPLAY) === '1';

      // if not shown before, set the value in localStorage,
      // because we only want to show this info once
      if (!this.shownBefore) {
        localStorage?.setItem(SEARCH_SHORTCUTS_INFO_DISPLAY, '1');
      }
    }

    this.show = passed && !this.shownBefore;
  },
  methods: {
    createQueryWithTag(shortcut, html = true) {
      if (html) {
        return `<strong>${this.identifier}${shortcut.tag}</strong> ${this.initialQueryValue}`;
      }

      return `${this.identifier}${shortcut.tag} ${this.initialQueryValue}`;
    },
    onClick(shortcut) {
      if (this.query !== this.createQueryWithTag(shortcut, false)) {
        this.$router.replace({
          query: {
            ...this.$route.query,
            q: addTagToQuery(this.query, shortcut.tag, true),
          },
        });
      }
    },
    trackLinkBubbleClick(index) {
      if (this.experimentHasValue(searchShortcuts.variantA)) {
        // the position is irrelevant if job A is the active variant
        return this.trackSearchShortcutInfoBoxClick();
      }

      // the position we're tracking will either be 1 or 2,
      // so we are adding +1 and also need to convert to a string
      return this.trackSearchShortcutInfoBoxClick(`${index + 1}`);
    },
    experimentHasValue(value) {
      return this.experiment && this.experiment?.value === value;
    },
  },
};
</script>

<style lang="scss" scoped>
.search-shortcuts-info-box {
  background: var(--color-background-quaternary);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: $space-m;
  gap: $space-1s;

  &.position-bottom {
    margin-top: $space-m;
  }

  @include desktop {
    background: var(--color-background-secondary);
    border-radius: $border-radius-1l;
  }
}

.search-shortcuts-info-box__top {
  padding-bottom: $space-1s;
}

.search-shortcuts-info-box__link-bubble-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;
  gap: $space-2s;

  @include mobile-l {
    flex-direction: row;
    align-items: center;
    gap: $space-1s;
  }
}

.search-shortcuts-info-box__link-bubble-container:nth-of-type(2n + 1) {
  padding-top: $space-1s;

  // we only want this padding for the 2nd container on mobile
  @include mobile-l {
    padding-top: initial;
  }
}

.search-shortcuts-info-box__link-bubble {
  display: flex;
  justify-content: space-between;
  align-self: stretch;

  @include mobile-l {
    flex: 2;
  }

  @include tablet {
    flex: 3;
  }

  &:visited {
    color: var(--color-link-results-default);
  }

  .search-shortcuts-info-box__icon {
    color: var(--color-text-secondary);
  }
}

.search-shortcuts-info-box__description {
  color: var(--color-text-secondary);

  @include mobile-l {
    flex: 1;
    padding-left: $space-1s;
  }
}

.search-shortcuts-info-box__more {
  padding-top: $space-1s;
}
</style>
